<template>
    <div>
    <v-layout column data-testid="facility-details">
        <v-layout>
            <v-flex xs4 class="pr-1">
                <v-card outlined class="outlined mb-6 elevation-0 rounded-lg" style="min-height:392px;">
                    <v-toolbar class="text-h5 elevation-2">Facility Info</v-toolbar>
                    <v-flex class="px-2 py-1 ml-2">
                        <v-layout class="text-h6" column>
                            <v-layout class="mt-3 mb-2">
                                <v-flex xs5>Location Code: </v-flex>
                                <v-flex xs7>{{ facility.location_code }}</v-flex>
                            </v-layout>
                            <v-layout class="mt-2 mb-1">
                                <v-flex xs5>Address: </v-flex>
                                <v-flex xs7>{{ facility.street_address }}</v-flex>
                            </v-layout>
                            <v-layout class="mb-2">
                                <v-flex xs5></v-flex>
                                <v-flex xs7>{{ facility.city }}, {{ facility.state }}, {{ facility.postal_code }}</v-flex>
                            </v-layout>
                            <v-layout class="my-2">
                                <v-flex xs5>Phone: </v-flex>
                                <v-flex xs7>{{ addDashes(facility.phone) }}</v-flex>
                            </v-layout>
                            <v-layout class="my-2">
                                <v-flex xs5>E-mail: </v-flex>
                                <v-flex xs7>{{ facility.contact_email }}</v-flex>
                            </v-layout>
                            <v-layout class="mt-2">
                                <v-flex xs5>Price Range: </v-flex>
                                <v-flex xs7>${{ facility.min_unit_price }} - ${{ facility.max_unit_price }}</v-flex>
                            </v-layout>
                        </v-layout>
                    </v-flex>
                </v-card>
            </v-flex>
            <v-flex xs4 class="px-1">
                <v-card outlined class="outlined mb-6 elevation-0 rounded-lg" style="min-height:392px;">
                    <v-toolbar class="text-h5 elevation-2">Facility Hours</v-toolbar>
                    <v-layout class="ma-4 pt-2">
                        <v-flex xs6>
                            <div class="text-h6 mb-3 font-weight-bold">Office Hours</div>
                            <v-layout
                                class="mt-1 subheading"
                                v-for="(hours, day) in facility.external_office_hours"
                                :key="hours.i"
                            >
                                <v-flex xs5>{{ capitalize(day) }}</v-flex>
                                <v-flex v-if="hours.enabled && hours.open == hours.close">Open 24 Hours</v-flex>
                                <v-flex v-else-if="hours.enabled && hours.open != hours.close">{{ hours.open }} - {{ hours.close }}</v-flex>
                                <v-flex v-else>Closed</v-flex>
                            </v-layout>
                        </v-flex>
                        <v-divider vertical class="mr-3" />
                        <v-flex xs6 align-content-center>
                            <div class="text-h6 mb-3 font-weight-bold">Access Hours</div>
                            <v-layout
                                class="mt-1 subheading"
                                v-for="(hours, day) in facility.external_access_hours"
                                :key="hours.i"
                            >
                                <v-flex xs5>{{ capitalize(day) }}</v-flex>
                                <v-flex v-if="hours.enabled && hours.open == hours.close">24 Hour Access</v-flex>
                                <v-flex v-else-if="hours.enabled && hours.open != hours.close">{{ hours.open }} - {{ hours.close }}</v-flex>
                                <v-flex v-else>Closed</v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                    <v-card-text class="pt-2 pb-5 my-0">
                        <i>All times are {{ getTimezoneName(facility.access_timezone) }}</i>
                    </v-card-text>
                </v-card>
            </v-flex>
            <v-flex xs4 class="pl-1">
                <v-card outlined class="outlined mb-6 elevation-0 rounded-lg" style="min-height:392px;">
                    <v-toolbar class="text-h5 elevation-2">Map view</v-toolbar>
                    <GmapMap
                        v-if="location"
                        class="mt-3"
                        :center="location"
                        :zoom="13"
                        map-type-id="roadmap"
                        style="height:18rem;"
                    >
                        <gmap-info-window
                            :options="infoOptions"
                            :position="location"
                            :opened="infoWinOpen"
                            @closeclick="infoWinOpen=false"
                        >
                            <div class="secondary--text pa-2">
                                <div class="secondary--text text-body-2 mb-1">
                                    {{ facility.name }}
                                </div>
                                <div class="secondary--text text-body-1">
                                    <div>{{ facility.street_address }}</div>
                                    <div>{{ facility.city }}, {{ facility.state }} {{ facility.postal_code }}</div>
                                </div>
                            </div>
                        </gmap-info-window>
                        <GmapMarker
                            :position="location"
                            :clickable="true"
                            :draggable="true"
                            @click="toggleInfoWindow"
                        />
                    </GmapMap>
                </v-card>
            </v-flex>
        </v-layout>

<v-card outlined class="outlined mb-6 elevation-0 rounded-lg" style="min-height:10.25rem;">
            <v-toolbar class="text-h5 elevation-2">Notes</v-toolbar>
            <v-flex class="pa-4 mt-2">
                <v-layout column>
                    <v-flex v-if="facility.notes && facility.notes.length" class="mt-1">
                        <v-layout column v-for="note of facility.notes" :key="note.id">
                            <span v-if="note.content" v-html="note.content" />
                            <span v-else>There are no current notes for this facility.</span>
                            <span class="text-caption font-italic mt-1">Last updated {{ timeStamp(note.updated_at) }}</span>
                        </v-layout>
                    </v-flex>
                    <v-flex v-else class="subheading pa-3">
                        There are no notes to display for this facility.
                    </v-flex>
                </v-layout>
            </v-flex>
        </v-card>

<v-card outlined class="outlined mb-6 elevation-0 rounded-lg" style="min-height:10.25rem;">
            <v-toolbar class="text-h5 elevation-2">Facility Images</v-toolbar>
            <v-layout>
                <v-flex v-for="image of facility.images" :key="image.i" class="pa-2 mt-2" xs4>
                    <v-img
                        :src="imagePath + image.url"
                        :alt="image.alt"
                     />
                </v-flex>
            </v-layout>
        </v-card>
    </v-layout>
    </div>
</template>

<script>
/* eslint-disable no-return-assign */
/* eslint-disable no-undef */
import moment from "moment";
import { getTimezoneName } from "../helpers/get-timezone-name";

export default {
  props: ["facility"],

  data() {
    return {
      imagePath: process.env.VUE_APP_PHOTO_BASE_PATH + "/",
      infoWinOpen: true,
      infoOptions: {
        disableAutoPan: true
      }
    };
  },

  methods: {
    capitalize(day) {
      if (!day) {
        return "";
      };
      if (!day.charAt) {
        return day;
      }
      return day.charAt(0).toUpperCase() + day.substr(1);
    },

    timeStamp(date) {
      return moment.utc(date).local().fromNow();
    },

    toggleInfoWindow() {
      return this.infoWinOpen ? this.infoWinOpen = false : this.infoWinOpen = true;
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    },

    getTimezoneName
  },

  computed: {
    google() {
      return { google: gmapApi() };
    },

    loading() {
      return this.$store.state.facilities.facilityLoading;
    },

    location() {
      return this.facility ? { lat: parseFloat(this.facility.latitude), lng: parseFloat(this.facility.longitude) } : null;
    }
  }
};
</script>
